/**
 * TOSS Web Application - Router Configuration
 *
 * React Router v6 configuration with:
 * - Protected routes for authenticated users
 * - Role-based access control
 * - Lazy loading for code splitting
 * - Layout integration
 *
 * Based on PROJECT_DOCUMENTATION.md Section 22 (Frontend Structure).
 */

import {
  createBrowserRouter,
  RouterProvider,
  type RouteObject,
} from 'react-router-dom';
import { ROUTES } from '@/constants';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { InternalOnlyRoute } from './InternalOnlyRoute';
import { ProtectedRoute } from './ProtectedRoute';
import {
  SuspenseWrapper,
  LazyPage,
  HomeRedirect,
  ProtectedLayout,
  MessengerLayout,
  PublicLayout,
  RouteErrorBoundary,
} from './layouts';
import { authRoutes } from './routes/auth.routes';
import { applicationsRoutes } from './routes/applications.routes';
import { ptoRoutes } from './routes/pto.routes';
import { tasksRoutes } from './routes/tasks.routes';
import { expensesRoutes } from './routes/expenses.routes';
import { workersRoutes } from './routes/workers.routes';
import { fmoRoutes } from './routes/fmo.routes';
import { payrollRoutes } from './routes/payroll.routes';
import { directoryRoutes } from './routes/directory.routes';
import { instructionsRoutes } from './routes/instructions.routes';
import { miscRoutes } from './routes/misc.routes';
import { adminRoutes } from './routes/admin.routes';
import { clientRoutes } from './routes/client.routes';

// ============================================================================
// Lazy Loaded Pages (with auto-retry on chunk load failure)
// ============================================================================

// Messenger pages
const MessengerPage = lazyWithRetry(() => import('@/features/messenger/pages/MessengerPage'));

// Share target page (Web Share Target API entry point)
const SharePage = lazyWithRetry(() => import('@/features/share/pages/SharePage'));

// Error pages
const NotFoundPage = lazyWithRetry(() => import('@/features/errors/NotFoundPage'));
const ForbiddenPage = lazyWithRetry(() => import('@/features/errors/ForbiddenPage'));

// ============================================================================
// Router Configuration
// ============================================================================

/**
 * Application router configuration
 *
 * Route structure:
 * - Public routes (no auth required): /login, /register, /forgot-password
 * - Protected routes (auth required): all others
 * - Role-based routes: specific modules based on permission matrix
 */
const routes: RouteObject[] = [
  // ========================================
  // Public Routes (Auth)
  // ========================================
  {
    element: <PublicLayout />,
    children: authRoutes,
  },

  // ========================================
  // Home redirect (role-aware) — top-level, deliberately NOT inside ProtectedLayout
  //
  // `HomeRedirect` renders nothing but a <Navigate>, so it needs no chrome — and it must
  // not sit under `ProtectedLayout`, where `AppLayout` (the internal shell) renders ABOVE
  // the guard. `/` is exactly where an external customer lands: vite.config.ts sets the
  // PWA `scope` and `start_url` to `/`. Nested, a client session would paint the internal
  // sidebar/header for a frame, fire two `/api/lk/*` 403s from `Header`, and open a
  // `/hubs/messenger` connection that OUTLIVES the redirect (that hub is a singleton,
  // stopped only on logout) before ever reaching `/client`.
  //
  // Guarded by `ProtectedRoute`, NOT by `InternalOnlyRoute`: a client here must be sent to
  // their own portal (`getDefaultRoute(UserRole.Client) === ROUTES.client.dashboard`), not
  // to /403. This is the same auth gate `ProtectedLayout` applied before, minus the shell —
  // including the `isPermissionsLoaded` wait `getDefaultRoute` depends on, since it reads
  // permission keys for every internal role.
  // ========================================
  {
    path: ROUTES.home,
    element: (
      <ProtectedRoute>
        <HomeRedirect />
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.dashboard,
    element: (
      <ProtectedRoute>
        <HomeRedirect />
      </ProtectedRoute>
    ),
  },

  // ========================================
  // Protected Routes (internal audience only)
  //
  // `InternalOnlyRoute` wraps the LAYOUT, not the page elements — the same reasoning the
  // messenger branch below spells out: `ProtectedLayout` renders `AppLayout`, so guarding
  // the children would still mount the internal shell (Sidebar, Header polling
  // `/api/lk/*`, CommandPalette, CalculatorPanel, the global `useMessengerSignalR()`
  // subscription) for a client session before any redirect fires. Most pages under here
  // are denied by `PermissionRoute` on their own (a client session resolves to an empty
  // key set) — but that denial happens INSIDE the shell, which is what this guard exists
  // to prevent. It also covers `/profile` and `/notifications`, which no permission key
  // gates at all.
  // ========================================
  {
    element: (
      <InternalOnlyRoute>
        <ProtectedLayout />
      </InternalOnlyRoute>
    ),
    children: [
      ...applicationsRoutes,
      ...ptoRoutes,
      ...tasksRoutes,
      ...expensesRoutes,
      ...workersRoutes,
      ...fmoRoutes,
      ...payrollRoutes,
      ...directoryRoutes,
      ...instructionsRoutes,
      ...miscRoutes,
      ...adminRoutes,
    ],
  },

  // ========================================
  // Client Portal Routes (external customer audience)
  //
  // Its own top-level branch, never inside ProtectedLayout: that layout wraps
  // AppLayout — the internal shell. The portal brings its own chrome and its own
  // role-based guard. See routes/client.routes.tsx.
  // ========================================
  ...clientRoutes,

  // ========================================
  // Messenger Routes (viewport-locked layout)
  //
  // InternalOnlyRoute wraps the LAYOUT, not the two page elements: MessengerLayout
  // renders AppLayout, so guarding only the children would still mount the internal
  // shell (Sidebar, Header polling /api/lk/*, CommandPalette, CalculatorPanel,
  // useMessengerSignalR) for a client session before the redirect fires. These two
  // routes are gated by ProtectedRoute alone — phase0-part2 §2.5 listed /profile,
  // /notifications and /share and missed them, so a customer typing /messenger got the
  // internal messenger's chrome on screen (its data 403s, the shell does not).
  // ========================================
  {
    element: (
      <InternalOnlyRoute>
        <MessengerLayout />
      </InternalOnlyRoute>
    ),
    children: [
      {
        path: ROUTES.messenger.list,
        element: <MessengerPage />,
      },
      {
        path: ROUTES.messenger.chat,
        element: <MessengerPage />,
      },
    ],
  },

  // ========================================
  // Web Share Target landing
  // ========================================
  {
    // No permission key gates this route (any internal role can share into TOSS) —
    // InternalOnlyRoute is what keeps a client session out (phase0-part2 §2.5); it
    // still provides the ProtectedRoute auth/loading gates via composition.
    path: '/share',
    element: (
      <InternalOnlyRoute>
        <SuspenseWrapper>
          <SharePage />
        </SuspenseWrapper>
      </InternalOnlyRoute>
    ),
  },

  // ========================================
  // Error Routes
  // ========================================
  {
    path: ROUTES.errors.forbidden,
    element: (
      <LazyPage>
        <ForbiddenPage />
      </LazyPage>
    ),
  },
  {
    path: ROUTES.errors.notFound,
    element: (
      <LazyPage>
        <NotFoundPage />
      </LazyPage>
    ),
  },

  // ========================================
  // Catch-all (404)
  // ========================================
  {
    path: '*',
    element: (
      <LazyPage>
        <NotFoundPage />
      </LazyPage>
    ),
  },
];

const router = createBrowserRouter([
  {
    // Pathless root route: its errorElement catches render errors from every
    // route below, replacing the router's default English crash screen.
    errorElement: <RouteErrorBoundary />,
    children: routes,
  },
], {
  // React Router v7 future flags for createBrowserRouter
  // Opt-in early to v7 behavior to suppress deprecation warnings
  future: {
    v7_relativeSplatPath: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
  },
});

// ============================================================================
// Router Provider Component
// ============================================================================

/**
 * App router provider component
 *
 * Wraps the application with React Router provider.
 * Should be placed inside the main App component or providers.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <AppProviders>
 *       <AppRouter />
 *     </AppProviders>
 *   );
 * }
 * ```
 */
export function AppRouter(): JSX.Element {
  return (
    <RouterProvider
      router={router}
      // React Router v7 future flag for RouterProvider
      // Wraps state updates in React.startTransition
      future={{ v7_startTransition: true }}
    />
  );
}

export default AppRouter;
